import { z } from "zod";
import Header from "../../components/Header/Header";
import {
  Control,
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo } from "react";
import SettingsSection from "../../components/SettingsSection";
import Button from "../../../../ui/Button/Button";
import TextInput from "../../../../ui/TextInput/TextInput";
import { Close, Plus } from "../../../../ui/Icon/Icon";
import IconButton from "../../../../ui/IconButton/IconButton";
import classNames from "classnames";
import Feedback from "../../../../ui/Feedback/Feedback";
import { getFragmentData, graphql } from "../../../../__generated__/gql";
import { useMutation, useQuery, useSubscription } from "urql";
import RequestScrape from "./RequestScrape/RequestScrape";
import { ScrapeStatus, Volume } from "../../../../__generated__/gql/graphql";
import MultiTextInput from "../../../../ui/MultiTextInput/MultiTextInput";
import Label from "../../../../ui/Label/Label";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal/UnsavedChangesModal";
import Loader from "../../../../ui/Loader/Loader";
import { useToast } from "../../../../ui/Toast/ToastContext";
import Switch from "../../../../ui/Switch/Switch";
import Select from "../../../../ui/Select/Select";
import styles from "./Website.module.scss";

const WebsiteSettingsFragment = graphql(/* GraphQL */ `
  fragment WebsiteSettings on WebsiteSettings {
    websites
    lastScrapedAt
    scrapeStatus
    allowAllInReference
    includeSomeInReference
    excludeSomeFromReference
    includePathsInReference
    includePagesInReference
    excludePathsFromReference
    excludePagesFromReference

    allowAllInInternalLinking
    includeSomeInInternalLinking
    excludeSomeFromInternalLinking
    internalLinkingVolume
    includePathsInInternalLinking
    includePagesInInternalLinking
    excludePathsFromInternalLinking
    excludePagesFromInternalLinking

    includeSomeInPriorityLinking
    excludeSomeFromPriorityLinking
    includePathsInPriorityLinking
    includePagesInPriorityLinking
    excludePathsFromPriorityLinking
    excludePagesFromPriorityLinking
  }
`);

const ScrapeStatusUpdatedSubscription = graphql(/* GraphQL */ `
  subscription scrapeStatusUpdated {
    websiteSettingsUpdated {
      websites
      lastScrapedAt
      scrapeStatus
      allowAllInReference
      includeSomeInReference
      excludeSomeFromReference
      includePathsInReference
      includePagesInReference
      excludePathsFromReference
      excludePagesFromReference

      allowAllInInternalLinking
      includeSomeInInternalLinking
      excludeSomeFromInternalLinking
      internalLinkingVolume
      includePathsInInternalLinking
      includePagesInInternalLinking
      excludePathsFromInternalLinking
      excludePagesFromInternalLinking

      includeSomeInPriorityLinking
      excludeSomeFromPriorityLinking
      includePathsInPriorityLinking
      includePagesInPriorityLinking
      excludePathsFromPriorityLinking
      excludePagesFromPriorityLinking
    }
  }
`);

const WebsiteSettingsQuery = graphql(/* GraphQL */ `
  query websiteSettings {
    websiteSettings {
      ...WebsiteSettings
    }
    scrapedWebpages {
      totalCount
    }
  }
`);

const ScrapeWebpagesCountQuery = graphql(/* GraphQL */ `
  query scrapedWebpagesCount {
    scrapedWebpages {
      totalCount
    }
  }
`);

const UpdateWebsiteSettingsMutation = graphql(/* GraphQL */ `
  mutation updateWebsiteSettings($input: UpdateWebsiteSettingsInput!) {
    updateWebsiteSettings(input: $input) {
      ...WebsiteSettings
    }
  }
`);

const schema = z.object({
  websites: z.array(z.object({ url: z.string().url() })),
  referencePages: z.object({
    allowAll: z.boolean(),
    includeSome: z.boolean(),
    includePaths: z.array(z.string()).optional(),
    includePages: z.array(z.string()).optional(),
    excludeSome: z.boolean(),
    excludePaths: z.array(z.string()).optional(),
    excludePages: z.array(z.string()).optional(),
  }),
  internalLinkingPages: z.object({
    allowAll: z.boolean(),
    includeSome: z.boolean(),
    includePaths: z.array(z.string()).optional(),
    includePages: z.array(z.string()).optional(),
    excludeSome: z.boolean(),
    excludePaths: z.array(z.string()).optional(),
    excludePages: z.array(z.string()).optional(),
  }),
  priorityLinkingPages: z.object({
    includeSome: z.boolean(),
    includePaths: z.array(z.string()).optional(),
    includePages: z.array(z.string()).optional(),
    excludeSome: z.boolean(),
    excludePaths: z.array(z.string()).optional(),
    excludePages: z.array(z.string()).optional(),
  }),
  internalLinkingVolume: z.enum([
    Volume.None,
    Volume.Low,
    Volume.Medium,
    Volume.High,
  ]),
});

type WebsiteFormData = z.infer<typeof schema>;

function Exclusions({
  control,
  name,
}: {
  control: Control<WebsiteFormData>;
  name: keyof Pick<
    WebsiteFormData,
    "internalLinkingPages" | "referencePages" | "priorityLinkingPages"
  >;
}) {
  const value = useWatch({ control, name });
  const { setValue } = useFormContext<WebsiteFormData>();

  return (
    <div className={styles.toneAndLanguage}>
      {name !== "priorityLinkingPages" && (
        <Controller
          name={`${name}.allowAll`}
          control={control}
          render={({ field }) => (
            <div className={styles.radios}>
              <label htmlFor={`${name}.allowAll`} className={styles.radioLabel}>
                <Switch
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);

                    if (value) {
                      setValue(`${name}.includeSome`, false);
                      setValue(`${name}.excludeSome`, false);
                    }
                  }}
                />
                Allow all pages
              </label>
            </div>
          )}
        />
      )}
      <Controller
        name={`${name}.includeSome`}
        control={control}
        render={({ field }) => (
          <label htmlFor={`${name}.includeSome`} className={styles.radioLabel}>
            <Switch
              id={field.name}
              checked={field.value}
              onCheckedChange={(value) => {
                field.onChange(value);

                if (value && name !== "priorityLinkingPages") {
                  setValue(`${name}.allowAll`, false);
                }
              }}
            />
            Allow some pages
          </label>
        )}
      />

      {!!value?.includeSome && (
        <>
          <Controller
            name={`${name}.includePaths`}
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <div style={{ width: "100%" }}>
                  <div className={styles.inputRow}>
                    <Label label="URL paths containing" htmlFor={field.name}>
                      <MultiTextInput
                        id={field.name}
                        {...field}
                        error={!!error?.message}
                      />
                    </Label>
                  </div>
                  {error?.message && (
                    <Feedback className={styles.feedback}>
                      {error.message}
                    </Feedback>
                  )}
                </div>
              );
            }}
          />
          <Controller
            name={`${name}.includePages`}
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <div style={{ width: "100%" }}>
                  <div className={styles.inputRow}>
                    <Label
                      label="URL paths matching exactly"
                      htmlFor={field.name}
                    >
                      <MultiTextInput
                        id={field.name}
                        {...field}
                        error={!!error?.message}
                      />
                    </Label>
                  </div>
                  {error?.message && (
                    <Feedback className={styles.feedback}>
                      {error.message}
                    </Feedback>
                  )}
                </div>
              );
            }}
          />
        </>
      )}

      <Controller
        name={`${name}.excludeSome`}
        control={control}
        render={({ field }) => (
          <label htmlFor={`${name}.excludeSome`} className={styles.radioLabel}>
            <Switch
              id={field.name}
              checked={field.value}
              onCheckedChange={(value) => {
                field.onChange(value);

                if (value && name !== "priorityLinkingPages") {
                  setValue(`${name}.allowAll`, false);
                }
              }}
            />
            Exclude some pages
          </label>
        )}
      />

      {!!value?.excludeSome && (
        <>
          <Controller
            name={`${name}.excludePaths`}
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <div style={{ width: "100%" }}>
                  <div className={styles.inputRow}>
                    <Label label="URL paths containing" htmlFor={field.name}>
                      <MultiTextInput
                        id={field.name}
                        {...field}
                        error={!!error?.message}
                      />
                    </Label>
                  </div>
                  {error?.message && (
                    <Feedback className={styles.feedback}>
                      {error.message}
                    </Feedback>
                  )}
                </div>
              );
            }}
          />
          <Controller
            name={`${name}.excludePages`}
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <div style={{ width: "100%" }}>
                  <div className={styles.inputRow}>
                    <Label
                      label="URL paths matching exactly"
                      htmlFor={field.name}
                    >
                      <MultiTextInput
                        id={field.name}
                        {...field}
                        error={!!error?.message}
                      />
                    </Label>
                  </div>
                  {error?.message && (
                    <Feedback className={styles.feedback}>
                      {error.message}
                    </Feedback>
                  )}
                </div>
              );
            }}
          />
        </>
      )}
    </div>
  );
}

function WebsiteSettingsForm({
  defaultValues,
  onSubmit,
  lastScrapedAt,
  scrapedWebpagesCount,
  scrapeStatus,
}: {
  defaultValues: Partial<WebsiteFormData>;
  onSubmit: (data: Partial<WebsiteFormData>) => Promise<void>;
  lastScrapedAt?: string;
  scrapedWebpagesCount?: number;
  scrapeStatus?: ScrapeStatus;
}) {
  const methods = useForm<WebsiteFormData>({
    resolver: zodResolver(schema),
    defaultValues,
    reValidateMode: "onChange",
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty, dirtyFields },
    getFieldState,
  } = methods;

  useEffect(() => {
    if (!isDirty) {
      reset(defaultValues);
    }
  }, [defaultValues, isDirty, reset]);

  const websitesFieldState = getFieldState("websites");

  const websitesFieldArray = useFieldArray({ control, name: "websites" });

  const onSubmitWrapper = useCallback(
    async (data: Partial<WebsiteFormData>) => {
      const updatedData: Partial<WebsiteFormData> = data;

      Object.keys(updatedData).forEach((key) => {
        if (!(key in dirtyFields)) {
          delete updatedData[key as keyof WebsiteFormData];
        }
      });

      await onSubmit(updatedData);

      reset(data);
    },
    [dirtyFields, onSubmit, reset],
  );

  return (
    <FormProvider {...methods}>
      <UnsavedChangesModal isDirty={isDirty} />
      <form onSubmit={handleSubmit(onSubmitWrapper)}>
        <Header
          title="Website & Linking"
          description="Control how Macaw interacts with your website"
          isSubmitting={isSubmitting}
          isDirty={isDirty}
          onCancel={() => reset()}
        />
        <SettingsSection
          label="Websites"
          description="We'll keep a up to date copy of your website(s)"
        >
          <div
            className={classNames(styles.inputSection, styles.websitesSection)}
          >
            {websitesFieldArray.fields.map((field, index) => (
              <Controller
                key={field.id}
                name={`websites.${index}.url`}
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <div style={{ width: "100%" }}>
                      <div className={styles.inputRow}>
                        <TextInput {...field} error={!!error?.message} />
                        <IconButton
                          aria-label="Remove website"
                          disabled={websitesFieldArray.fields.length === 1}
                          IconComponent={Close}
                          onClick={() => websitesFieldArray.remove(index)}
                        />
                      </div>
                      {error?.message && (
                        <Feedback className={styles.feedback}>
                          {error.message}
                        </Feedback>
                      )}
                    </div>
                  );
                }}
              />
            ))}
            <Button
              className={styles.addButton}
              variant="link"
              size="small"
              leftIcon={<Plus />}
              onClick={() => websitesFieldArray.append({ url: "" })}
            >
              Add another
            </Button>
          </div>
        </SettingsSection>
        <SettingsSection
          label="Website scraping"
          description="We will automatically check for updates every week"
        >
          <div
            className={classNames(styles.inputSection, styles.scrapeSection)}
          >
            <RequestScrape
              isDirty={isDirty}
              disabled={
                !defaultValues.websites?.some((website) => !!website?.url) ||
                websitesFieldState.isDirty
              }
              lastScrapedAt={lastScrapedAt}
              scrapedWebpagesCount={scrapedWebpagesCount}
              scrapeStatus={scrapeStatus}
            />
          </div>
        </SettingsSection>
        <SettingsSection
          label="Reference pages"
          description="Manage which pages Macaw gathers information from on your website"
        >
          <div className={styles.inputSection}>
            <Exclusions control={control} name="referencePages" />
          </div>
        </SettingsSection>
        <SettingsSection
          label="Internal linking pages"
          description="Manage which pages Macaw will use for internal linking"
        >
          <div className={styles.inputSection}>
            <Exclusions control={control} name="internalLinkingPages" />
          </div>
        </SettingsSection>
        <SettingsSection
          label="Priority linking pages"
          description="Manage which pages Macaw prioritizes when internal linking"
        >
          <div className={styles.inputSection}>
            <Exclusions control={control} name="priorityLinkingPages" />
          </div>
        </SettingsSection>
        <SettingsSection
          label="Internal linking"
          description="Manage internal linking settings"
        >
          <div className={styles.inputSection}>
            <Label
              label="Internal linking volume"
              tooltip={
                <>
                  <p>Controls the number of links you will see in articles.</p>
                  <p>Using a typical 2000 word article as an example:</p>
                  <ul>
                    <li>Low will include 0-5 links</li>
                    <li>Medium will include 0-10 links</li>
                    <li>High will include 0-15 links</li>
                  </ul>
                  <p>
                    Many factors contribute to the number of links that will be
                    included in an article:
                  </p>
                  <ul>
                    <li>
                      The number of pages available for linking in your
                      website(s)
                    </li>
                    <li>The length of the article</li>
                    <li>
                      How many available links are relevant to an articles
                      content
                    </li>
                    <li>The selected internal linking volume option</li>
                  </ul>
                </>
              }
            >
              <Controller
                name="internalLinkingVolume"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    data={[
                      { label: "None", value: Volume.None },
                      { label: "Low", value: Volume.Low },
                      { label: "Medium", value: Volume.Medium },
                      { label: "High", value: Volume.High },
                    ]}
                  />
                )}
              />
            </Label>
          </div>
        </SettingsSection>
      </form>
    </FormProvider>
  );
}

function Website() {
  const [scrapeStatusSubscription] = useSubscription({
    query: ScrapeStatusUpdatedSubscription,
  });
  const [, updateWebsiteSettings] = useMutation(UpdateWebsiteSettingsMutation);
  const [websiteSettingsResponse] = useQuery({
    query: WebsiteSettingsQuery,
    requestPolicy: "network-only",
  });
  const [, refetchScrapedWebpagesCount] = useQuery({
    query: ScrapeWebpagesCountQuery,
    requestPolicy: "network-only",
    pause: true,
  });
  const { showToast } = useToast();

  const websiteSettings = websiteSettingsResponse.data?.websiteSettings
    ? getFragmentData(
        WebsiteSettingsFragment,
        websiteSettingsResponse.data.websiteSettings,
      )
    : undefined;

  const defaultValues: WebsiteFormData = useMemo(() => {
    if (!websiteSettings) {
      return {
        websites: [{ url: "" }],
        referencePages: {
          allowAll: true,
          includeSome: false,
          excludeSome: false,
        },
        internalLinkingPages: {
          allowAll: true,
          includeSome: false,
          excludeSome: false,
        },
        priorityLinkingPages: {
          includeSome: false,
          excludeSome: false,
        },
        internalLinkingVolume: Volume.Medium,
      };
    }

    const {
      websites,
      allowAllInReference,
      includeSomeInReference,
      excludeSomeFromReference,
      excludePathsFromReference,
      excludePagesFromReference,
      includePathsInReference,
      includePagesInReference,
      allowAllInInternalLinking,
      includeSomeInInternalLinking,
      excludeSomeFromInternalLinking,
      excludePathsFromInternalLinking,
      excludePagesFromInternalLinking,
      includePathsInInternalLinking,
      includePagesInInternalLinking,
      includeSomeInPriorityLinking,
      excludeSomeFromPriorityLinking,
      excludePathsFromPriorityLinking,
      excludePagesFromPriorityLinking,
      includePathsInPriorityLinking,
      includePagesInPriorityLinking,
      internalLinkingVolume,
    } = websiteSettings;

    const referencePages: WebsiteFormData["referencePages"] = {
      allowAll: allowAllInReference,
      includeSome: includeSomeInReference,
      excludeSome: excludeSomeFromReference,
      excludePaths: excludePathsFromReference ?? undefined,
      excludePages: excludePagesFromReference ?? undefined,
      includePaths: includePathsInReference ?? undefined,
      includePages: includePagesInReference ?? undefined,
    };

    const internalLinkingPages: WebsiteFormData["internalLinkingPages"] = {
      allowAll: allowAllInInternalLinking,
      includeSome: includeSomeInInternalLinking,
      excludeSome: excludeSomeFromInternalLinking,
      excludePaths: excludePathsFromInternalLinking ?? undefined,
      excludePages: excludePagesFromInternalLinking ?? undefined,
      includePaths: includePathsInInternalLinking ?? undefined,
      includePages: includePagesInInternalLinking ?? undefined,
    };

    const priorityLinkingPages: WebsiteFormData["priorityLinkingPages"] = {
      includeSome: includeSomeInPriorityLinking,
      excludeSome: excludeSomeFromPriorityLinking,
      excludePaths: excludePathsFromPriorityLinking ?? undefined,
      excludePages: excludePagesFromPriorityLinking ?? undefined,
      includePaths: includePathsInPriorityLinking ?? undefined,
      includePages: includePagesInPriorityLinking ?? undefined,
    };

    return {
      websites:
        websites && websites.length > 0
          ? websites.map((url) => ({ url }))
          : [{ url: "" }],
      referencePages,
      internalLinkingPages,
      priorityLinkingPages,
      internalLinkingVolume,
    };
  }, [websiteSettings]);

  const onSubmit = useCallback(
    async (data: Partial<WebsiteFormData>) => {
      await updateWebsiteSettings({
        input: {
          websites: data.websites?.map((website) => website.url),

          internalLinkingVolume: data.internalLinkingVolume,
          allowAllInInternalLinking: data.internalLinkingPages?.allowAll,
          includeSomeInInternalLinking: data.internalLinkingPages?.includeSome,
          excludeSomeFromInternalLinking:
            data.internalLinkingPages?.excludeSome,
          excludePathsFromInternalLinking:
            data.internalLinkingPages?.excludePaths,
          excludePagesFromInternalLinking:
            data.internalLinkingPages?.excludePages,
          includePagesInInternalLinking:
            data.internalLinkingPages?.includePages,
          includePathsInInternalLinking:
            data.internalLinkingPages?.includePaths,

          allowAllInReference: data.referencePages?.allowAll,
          includeSomeInReference: data.referencePages?.includeSome,
          excludeSomeFromReference: data.referencePages?.excludeSome,
          excludePathsFromReference: data.referencePages?.excludePaths,
          excludePagesFromReference: data.referencePages?.excludePages,
          includePathsInReference: data.referencePages?.includePaths,
          includePagesInReference: data.referencePages?.includePages,

          includeSomeInPriorityLinking: data.priorityLinkingPages?.includeSome,
          excludeSomeFromPriorityLinking:
            data.priorityLinkingPages?.excludeSome,
          excludePathsFromPriorityLinking:
            data.priorityLinkingPages?.excludePaths,
          excludePagesFromPriorityLinking:
            data.priorityLinkingPages?.excludePages,
          includePathsInPriorityLinking:
            data.priorityLinkingPages?.includePaths,
          includePagesInPriorityLinking:
            data.priorityLinkingPages?.includePages,
        },
      });
      showToast({
        title: "Website & linking settings saved",
        variant: "success",
      });
    },
    [showToast, updateWebsiteSettings],
  );

  useEffect(() => {
    if (
      scrapeStatusSubscription.data?.websiteSettingsUpdated.scrapeStatus ===
      ScrapeStatus.Finished
    ) {
      refetchScrapedWebpagesCount({ requestPolicy: "network-only" });
    }
  }, [
    refetchScrapedWebpagesCount,
    scrapeStatusSubscription.data?.websiteSettingsUpdated.scrapeStatus,
  ]);

  if (websiteSettingsResponse.fetching) {
    return (
      <div
        style={{
          padding: "32px 0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <WebsiteSettingsForm
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      lastScrapedAt={websiteSettings?.lastScrapedAt ?? undefined}
      scrapedWebpagesCount={
        websiteSettingsResponse.data?.scrapedWebpages?.totalCount ?? undefined
      }
      scrapeStatus={websiteSettings?.scrapeStatus ?? undefined}
    />
  );
}

export default Website;
